import { findEffectiveWage, generatePositionWageValues } from '@ubeya/shared-web/utils/positions';
import moment from 'moment';

export const wageCellFormatter = ({
  value,
  rowData,
  t,
  key,
  currency = true,
  showDefaultWages = true,
  isFlatWage,
  formattingMap,
  mappedPositions,
  includeZero = false
}) => {
  const positionData = mappedPositions[rowData?.positionId];
  const selectedWage = generatePositionWageValues({
    wage: { ...(positionData?.wage || { hourlyWage: null, flatWage: null, flatHours: null }) },
    wages: positionData?.wages || [],
    externalWage: {
      ...(positionData?.clientWage || { hourlyWage: null, flatWage: null, flatHours: null })
    },
    externalWageKey: 'clientWage'
  });

  const defaultWageByKey = selectedWage?.wage?.[key];

  const displayDefaultWages = showDefaultWages
    ? (defaultWageByKey || defaultWageByKey === 0) &&
      (rowData.hourlyWage === null || rowData.hourlyWage === 0) &&
      (isFlatWage
        ? (rowData.flatWage === null || rowData.flatWage === 0) &&
          (rowData.flatHours === null || rowData.flatHours === 0)
        : true)
    : false;

  const cellValue = value ?? defaultWageByKey;
  const formattedValue = value !== null && currency ? formattingMap.currency(value, { includeZero }) : value;
  const formattedDefaultValue =
    defaultWageByKey !== null && currency
      ? formattingMap.currency(defaultWageByKey, { includeZero })
      : defaultWageByKey;

  if (cellValue === null || cellValue < 0) {
    return '-';
  }
  return displayDefaultWages
    ? formattedDefaultValue
      ? `${formattedDefaultValue} (${t('default')})`
      : ''
    : formattedValue;
};

/*
Takes a flat list of wage entries (each with an effectiveDate) and builds one table row: the "current" wage as the top-level fields,
everything else tucked into subRows with a past/future badge
*/

const groupWagesByEffectiveDate = (allWages) => {
  // Anchor row = the most recent wage already in effect. If none is in effect yet
  // (all wages are future), fall back to the earliest upcoming wage so the soonest
  // raise is shown as the main row rather than whichever entry the API returned first.
  const effectiveWage = findEffectiveWage(allWages);

  //The anchor is the row shown as the main (un-greyed) line
  const anchorWage =
    effectiveWage ??
    allWages.reduce((earliest, wage) =>
      moment(wage.effectiveDate).isBefore(moment(earliest.effectiveDate)) ? wage : earliest
    );

  const currentWageIndex = allWages.indexOf(anchorWage);
  const anchorDate = moment(anchorWage.effectiveDate);

  // Partition relative to the anchor so a non-anchor entry sharing the anchor's date (e.g. a
  // same-day correction) still renders as a past row. When no wage is in effect yet, every
  // non-anchor entry is upcoming.
  const futureWages = [];
  const pastWages = [];

  allWages.forEach((wage, index) => {
    // if im the anchor row, skip it
    if (index === currentWageIndex) {
      return;
    }
    if (effectiveWage == null || moment(wage.effectiveDate).isAfter(anchorDate)) {
      futureWages.push({ ...wage, wageStatus: 'future' });
    } else {
      pastWages.push({ ...wage, wageStatus: 'past' });
    }
  });

  return {
    ...anchorWage,
    ...(effectiveWage == null && { isUpcoming: true }), //  added only when the anchor is that future-fallback
    subRows: [...futureWages, ...pastWages]
  };
};

export const groupWages = ({ positions, data }) => {
  return positions.map((position) => {
    const allWages = data.filter(({ positionId }) => positionId === position);

    if (allWages.length === 0) {
      return { positionId: position };
    }

    return groupWagesByEffectiveDate(allWages);
  });
};

// Builds a single grouped row for the base wage so it can live inside the position-rates
// table, behaving like a position: current entry on top, past/future entries as subRows.
export const groupBaseWages = (baseWagesData) => {
  if (!baseWagesData || baseWagesData.length === 0) {
    return { isBaseWage: true };
  }

  return groupWagesByEffectiveDate(baseWagesData.map((wage) => ({ ...wage, isBaseWage: true })));
};
